import { ProgramForm, type ProgramFormRef } from "@modules/programs/components/programForm"
import { ProgramSelectView } from "@modules/programs/components/programHeader/programSelectView"
import { ProgramInfoWrapper } from "@modules/programs/components/programInfoWrapper"
import { ProgramPriceForm, type ProgramPriceFormRef } from "@modules/programs/components/programPriceForm"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { ProgramSasPaymentChoices } from "@modules/programs/programSas/components/programSasPaymentChoices"
import { ProgramSasWarning } from "@modules/programs/programSas/components/programSasWarning"
import { ProgramType } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { type SidebarNavigatorParams } from "@modules/sidebar/sidebarTypes"
import { Suspense, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { ErrorView } from "@/components/ui/layout/error/errorView"
import { LoadingSpinner } from "@/modules/ui/components/loading"

export const useProgramSidebar = () => {
  const { sidebarNavigateTo } = useSidebar()
  const programFormRef = useRef<ProgramFormRef>(null)
  const programPriceFormRef = useRef<ProgramPriceFormRef>(null)

  const navigateToProgramView = (programId: string, editionAllowed: boolean, args?: SidebarNavigatorParams) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramInfoWrapper programId={programId} editionAllowed={editionAllowed} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
      onHide: () => {
        return Promise.resolve(true)
      },
      ...args,
    })
  }

  const navigateToProgramCreationTypeSelection = () => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramSelectView />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })
  }

  const navigateToProgramCreation = (programType: ProgramType) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramForm programType={programType} ref={programFormRef} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: () => {
        return programFormRef.current ? programFormRef.current.onClose() : Promise.resolve(true)
      },
    })
  }

  const navigateToProgramEdition = (programType: ProgramType, program?: UserProgramDetailsEntity) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramForm program={program} programType={programType} ref={programFormRef} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: () => {
        return programFormRef.current ? programFormRef.current.onClose() : Promise.resolve(true)
      },
    })
  }

  const navigateToProgramPriceEdition = (programId: string, replaceLastComponent: boolean = false) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramPriceForm programId={programId} ref={programPriceFormRef} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceLastComponent,
      onHide: () => {
        return programPriceFormRef.current ? programPriceFormRef.current.onClose() : Promise.resolve(true)
      },
    })
  }

  const navigateToProgramPriceSasWarning = (programId: string) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramSasWarning programId={programId} />
          </Suspense>
        </ErrorBoundary>
      ),
    })
  }

  const navigateToProgramSasPaymentChoices = (programId: string) => {
    void sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramSasPaymentChoices programId={programId} />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceLastComponent: true,
    })
  }

  return {
    navigateToProgramView,
    navigateToProgramCreationTypeSelection,
    navigateToProgramCreation,
    navigateToProgramEdition,
    navigateToProgramPriceEdition,
    navigateToProgramPriceSasWarning,
    navigateToProgramSasPaymentChoices,
  }
}
